import React from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ColumnAlign = 'left' | 'center' | 'right';

export interface Column<T> {
  key: string;
  header: string;
  align?: ColumnAlign;
  /** Tailwind class(es) applied to both th and td, e.g. "w-48" */
  className?: string;
  /** Custom skeleton cell width (tailwind w-* class). Defaults to "w-24". */
  skeletonWidth?: string;
  render: (row: T) => React.ReactNode;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  rowKey: (row: T) => string | number;
  /** When true, replaces rows with animated skeleton placeholders. */
  loading?: boolean;
  /** Number of skeleton rows to show during loading. Default: 5 */
  skeletonRows?: number;
  /** Content shown when data is empty and not loading. */
  emptyMessage?: React.ReactNode;
  /** Extra row classes based on the data row. */
  rowClassName?: (row: T) => string;
}

// ─── Skeleton helpers ─────────────────────────────────────────────────────────

const Shimmer: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`animate-pulse bg-slate-200 rounded ${className}`} />
);

function SkeletonRows<T>({
  columns,
  count,
}: {
  columns: Column<T>[];
  count: number;
}) {
  return (
    <>
      {Array.from({ length: count }).map((_, rowIdx) => (
        <tr key={rowIdx} className="border-b border-slate-50">
          {columns.map((col) => {
            const align = col.align ?? 'left';
            const tdAlign =
              align === 'center'
                ? 'text-center'
                : align === 'right'
                ? 'text-right'
                : 'text-left';
            return (
              <td
                key={col.key}
                className={`px-5 py-4 ${tdAlign} ${col.className ?? ''}`}
              >
                <div
                  className={`inline-flex ${
                    align === 'center'
                      ? 'justify-center w-full'
                      : align === 'right'
                      ? 'justify-end w-full'
                      : ''
                  }`}
                  style={{ opacity: 1 - rowIdx * 0.1 }}
                >
                  <Shimmer className={`h-4 ${col.skeletonWidth ?? 'w-24'}`} />
                </div>
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

// ─── DataTable ────────────────────────────────────────────────────────────────

function DataTable<T>({
  columns,
  data,
  rowKey,
  loading = false,
  skeletonRows = 5,
  emptyMessage,
  rowClassName,
}: DataTableProps<T>) {
  const thAlign = (align?: ColumnAlign) =>
    align === 'center'
      ? 'text-center'
      : align === 'right'
      ? 'text-right'
      : 'text-left';

  const tdAlign = (align?: ColumnAlign) =>
    align === 'center'
      ? 'text-center'
      : align === 'right'
      ? 'text-right'
      : 'text-left';

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-slate-100">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-100">
            {columns.map((col) => (
              <th
                key={col.key}
                className={`px-5 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest ${thAlign(
                  col.align
                )} ${col.className ?? ''}`}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="divide-y divide-slate-50">
          {loading ? (
            <SkeletonRows columns={columns} count={skeletonRows} />
          ) : data.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="py-16 text-center text-slate-400 text-sm"
              >
                {emptyMessage ?? 'No data found.'}
              </td>
            </tr>
          ) : (
            data.map((row) => (
              <tr
                key={rowKey(row)}
                className={`hover:bg-blue-50/60 hover:shadow-[inset_3px_0_0_#3b82f6] transition-all ${
                  rowClassName?.(row) ?? ''
                }`}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`px-5 py-4 ${tdAlign(col.align)} ${
                      col.className ?? ''
                    }`}
                  >
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default DataTable;
